import { SPECIFICITY_WRAP } from 'client/widget-components/responsive-styles/toCSSObject';
import styled, { css } from 'styled-components';

// Duda's themed-button variables (mirrors `themeButtonStyles` in `styles.ts`
// for the cal.com atom). Driven entirely by `--btn-*` custom properties the
// site theme sets, so it re-skins with the site's button design.
const themeButtonStyles = css`
    background-color: var(--btn-bg-color);
    background-image: var(--btn-bg-image);
    background-position: var(--btn-bg-position);
    background-repeat: var(--btn-bg-repeat);
    background-size: var(--btn-bg-size);
    border-color: var(--btn-border-color);
    border-bottom-color: var(--btn-border-b-color);
    border-left-color: var(--btn-border-l-color);
    border-right-color: var(--btn-border-r-color);
    border-top-color: var(--btn-border-t-color);
    border-radius: var(--btn-border-radius);
    border-bottom-left-radius: var(--btn-border-bl-radius);
    border-bottom-right-radius: var(--btn-border-br-radius);
    border-top-left-radius: var(--btn-border-tl-radius);
    border-top-right-radius: var(--btn-border-tr-radius);
    border-width: var(--btn-border-width);
    border-bottom-width: var(--btn-border-b-width);
    border-left-width: var(--btn-border-l-width);
    border-right-width: var(--btn-border-r-width);
    border-top-width: var(--btn-border-t-width);
    direction: var(--btn-text-direction);
    text-align: var(--btn-text-align);
    color: var(--btn-text-color);
    font-family: var(--btn-text-font-family);
    font-size: var(--btn-text-font-size);
    font-weight: var(--btn-text-font-weight);
    text-decoration: var(--btn-text-decoration);
    font-style: var(--btn-text-font-style);
    padding: 8px 14px;

    &:hover {
        background: var(--btn-hover-bg);
        border-color: var(--btn-hover-border-color);
        border-bottom-color: var(--btn-hover-border-b-color);
        border-left-color: var(--btn-hover-border-l-color);
        border-right-color: var(--btn-hover-border-r-color);
        border-top-color: var(--btn-hover-border-t-color);
        color: var(--btn-hover-text-color) !important;
        font-weight: var(--btn-hover-text-font-weight);
        text-decoration: var(--btn-hover-text-decoration);
        font-style: var(--btn-hover-text-font-style);
    }
`;

/*
 * Classic-editor-specific. The editor ships a global, non-widget-scoped rule —
 * `div.dmInner input[type="text"], … , div.dmInner select` — that repaints every
 * field on the page: translucent grey fill, 2px white border, fixed 40px height.
 * At (0,2,2) it outranks the atom's own single-class field rule, so the box has
 * to be restated at a weight that clears it (the container class is repeated to
 * reach (0,4,1)). The legacy cal wrapper does the same thing in `styles.ts`.
 *
 * Values are read back from the atom's own tokens, so this stays a cascade fix
 * and not a second source of truth for the field design. The selector list
 * mirrors the editor rule's own targets, which is why checkboxes and radios are
 * absent — the editor rule never touches them.
 */
const classicEditorFieldOverrides = css`
    .booking-atom-booker-container.booking-atom-booker-container {
        textarea,
        input:not([type]),
        input[type='text'],
        input[type='email'],
        input[type='tel'],
        input[type='number'],
        input[type='date'],
        input[type='time'],
        input[type='datetime-local'],
        select {
            font-size: 0.875rem;
            background-color: var(--booker-field-bg, #fff);
            border: 1px solid var(--booker-border, #e5e7eb);
            height: auto;
            margin: 0;
            padding: 0.5rem 0.75rem;

            /*
             * Restated for the same reason: this block outranks the atom's own
             * state rules, so without these the focus ring and the read-only
             * fill would never apply.
             */
            &:focus {
                border-color: var(--booker-brand, #0061ff);
            }

            &:read-only,
            &:disabled {
                background-color: var(
                    --booker-field-disabled-bg,
                    var(--booker-hover, #f3f4f6)
                );
            }
        }
    }
`;

export const NativeBookingWrapper = styled.div`
    ${classicEditorFieldOverrides}

    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    background-color: ${({ theme }) => theme.bookerBackgroundColor};

    .booker-wrapper {
        display: flex;
        width: 100%;
        height: 100%;
    }

    /* the atom's root (.host) is the sole child while the Booker is mounted */
    .booker-wrapper > div {
        width: 100%;
        height: 100%;
    }

    /* Booker + skeleton mount points fill the wrapper. */
    .native-booker-atom,
    .native-booker-atom > div {
        width: 100%;
        height: 100%;
    }

    /*
     * While loading, keep the real booker mounted (so its lazy chunk + data
     * finish loading) but hidden, showing only the skeleton — the cal atom's
     * atoms-wrapper / booker-skeleton coordination, ported to the native mount
     * wrappers. Once the booker signals ready (isLoading flips false), the
     * wrapper drops "booker-loading" and the skeleton unmounts.
     */
    .booker-wrapper.booker-loading
        .native-booker-atom:not(.native-booker-skeleton) {
        display: none;
    }

    .booking-atom-booker-container {
        width: 100%;
        height: 100%;

        /*
         * Theme the whole atom through its own token vocabulary instead of
         * overriding hashed module classes — fields, calendar and time slots
         * all read these, so mapping the site theme here re-skins everything.
         */
        --booker-bg: ${({ theme }) => theme.bookerBackgroundColor};
        --booker-text: ${({ theme }) => theme.bookerPrimaryColor};
        --booker-text-muted: ${({ theme }) => theme.bookerPrimaryColor};
        --booker-border: ${({ theme }) => theme.bookerPrimaryColor};
        --booker-brand: ${({ theme }) => theme.bookerPrimaryColor};
        --booker-hover: ${({ theme }) => theme.bookerSecondaryColor};
        --booker-bg-emphasis: ${({ theme }) => theme.bookerPrimaryColor};
        --booker-on-emphasis: ${({ theme }) => theme.bookerBackgroundColor};

        /* Field placeholders — token system has no placeholder color. */
        input::placeholder,
        textarea::placeholder {
            color: ${({ theme }) => theme.bookerPrimaryColor};
            opacity: 0.25;
        }

        /*
         * Primary action uses the site's themed button rather than the token
         * emphasis colors. Scoped under the container + element so it outranks
         * the atom's own ".button.is-primary" module rule.
         */
        button.booking-atom-booking-form-confirm {
            ${themeButtonStyles}
        }

        /* The site chrome already shows the org/team, so hide the atom's. */
        .booking-atom-event-meta-organizer {
            display: none;
        }

        /* Meta icons + text follow the primary color (themeEventMetaStyles). */
        .booking-atom-event-meta svg,
        .booking-atom-event-meta p {
            color: ${({ theme }) => theme.bookerPrimaryColor};
        }

        /* gap between the meta detail rows. */
        .booking-atom-event-meta-details {
            gap: 16px;
        }

        /* Meta panel stretches to fill the column height. */
        .booking-atom-event-meta {
            height: 100%;
            min-height: fit-content;
        }

        /* Host provides navigation, so hide the form's own back button. */
        button.booking-atom-booking-form-back {
            display: none;
            opacity: 0;
            visibility: hidden;
            pointer-events: none;
            position: absolute;
            top: 0;
            left: 0;
            width: 0;
            height: 0;
            overflow: hidden;
        }

        /*
         * Secondary form buttons ("+ Add guests", guest remove) render as
         * plain text (themeConfirmationStyles), anchored on the form's global
         * class. Excludes the confirm button, which keeps its themed --btn-*
         * styling.
         */
        .booking-atom-booking-form
            button:not(.booking-atom-booking-form-confirm) {
            color: ${({ theme }) => theme.bookerPrimaryColor};
            border: unset;

            &:not(:disabled):hover {
                background-color: transparent;
            }
        }

        /*
         * Slots adopt the site's button format (mirrors
         * themeAvailableTimeSlotsStyles): site button radius, primary outline,
         * transparent fill, secondary hover. Targets the shared per-slot hook
         * so the live booker (<button>) and the static preview (<div>) style
         * identically.
         */
        .booking-atom-time-slots-slot {
            border-radius: var(--btn-border-radius);
            border-color: ${({ theme }) => theme.bookerPrimaryColor};
            background-color: transparent;
            color: ${({ theme }) => theme.bookerPrimaryColor};

            &:not(:disabled):hover {
                background-color: ${({ theme }) =>
                    theme.bookerSecondaryColor} !important;
            }
        }

        /*
         * AM / 24h format switcher — segmented control themed like the cal
         * atom. Native marks the active segment with aria-pressed (cal used
         * aria-checked).
         */
        .booking-atom-time-slots-toggle {
            background-color: transparent;
            border-color: ${({ theme }) => theme.bookerPrimaryColor};

            button {
                color: ${({ theme }) => theme.bookerPrimaryColor};
            }

            button[aria-pressed='true'] {
                color: ${({ theme }) => theme.bookerPrimaryColor};
                background-color: ${({ theme }) => theme.bookerSecondaryColor};
            }
        }
    }

    /*
     * Event title uses the site's booker-title typography. Kept at the wrapper
     * top level with SPECIFICITY_WRAP (mirrors the cal atom) — nesting the
     * evaluateStyles interpolation deeper drops the whole rule.
     */
    ${SPECIFICITY_WRAP}
    .booking-atom-event-meta-title {
        font-size: var(--font-size-h5);
        ${({ theme }) => theme.evaluateStyles({ styleKey: 'bookerTitle' })}
        color: ${({ theme }) => theme.bookerPrimaryColor} !important;
    }
`;
